/**
 * Enhanced Product – UI Interactions
 *
 * Handles purchase option toggling, supply options, promo price display,
 * tab translation retry logic, and overall section initialisation.
 *
 * Depends on (set by enhanced-product-information.liquid inline script):
 *   window.enhancedIsProductPage   — boolean
 *   window.enhancedProductData     — product data object (null on non-product pages)
 *   window.selectedVariantId       — currently-selected variant ID
 *
 * Calls functions from other asset files (loaded before this one):
 *   applyButtonTranslations()      — enhanced-product-helpers.js
 *   applyProductTranslations()     — enhanced-product-helpers.js
 *   applyDMOTranslations()         — enhanced-product-helpers.js
 *   applyProductTabTranslations()  — enhanced-product-helpers.js
 *   setupVariantSelectorHandlers() — enhanced-variants-handle.js
 *   setupCustomAddToCartHandlers() — enhanced-product-cart.js
 */

// ---------------------------------------------------------------------------
// Promo price
// ---------------------------------------------------------------------------

function updatePromoPrice() {
  const promoValueEl = document.querySelector('.promo-value');
  if (promoValueEl) {
    // App is now free — clear the price element
    promoValueEl.textContent = '';
    promoValueEl.style.display = 'none';
  }
}

// ---------------------------------------------------------------------------
// Tab translation retry (tabs may not exist in DOM yet on first call)
// ---------------------------------------------------------------------------

function applyTabTranslationsWithRetry(attempt = 0, maxAttempts = 10) {
  const tabSelectors = [
    '[data-block-id="overview-tab"]',
    '[data-block-id="benefits-tab"]',
    '[data-block-id="ingredients-tab"]',
    '[data-block-id="instructions-tab"]',
  ];
  const hasTabElements = tabSelectors.some((sel) => document.querySelector(sel));

  if (hasTabElements || attempt >= maxAttempts - 1) {
    if (typeof window.applyProductTabTranslations === 'function') {
      window.applyProductTabTranslations();
    }
  } else {
    setTimeout(() => applyTabTranslationsWithRetry(attempt + 1, maxAttempts), 200);
  }
}

// ---------------------------------------------------------------------------
// Purchase option (subscribe / one-time) handlers
// ---------------------------------------------------------------------------

function setupPurchaseOptionHandlers() {
  const enhancedSection = document.querySelector('.enhanced-product-section');
  if (!enhancedSection) return;

  const purchaseOptions = enhancedSection.querySelectorAll('input[name="purchase-option"]');
  const subscriptionCards = enhancedSection.querySelectorAll('.subscription-option');
  const addToCartBtn = enhancedSection.querySelector('.add-to-cart');

  if (purchaseOptions.length && addToCartBtn) {
    purchaseOptions.forEach((option) => {
      option.addEventListener('change', function () {
        updatePurchaseOptionDisplay(this.id);
      });
    });
  }

  // Make entire subscription cards clickable
  subscriptionCards.forEach((card) => {
    card.style.cursor = 'pointer';

    card.addEventListener('click', function (event) {
      if (!this.contains(event.target)) return;
      event.stopPropagation();
      const radioInput = this.querySelector('input[name="purchase-option"]');
      if (radioInput && !radioInput.checked) {
        radioInput.checked = true;
        updatePurchaseOptionDisplay(radioInput.id);
        updateSubscriptionCardStates();
      }
    });

    const radioContainer = card.querySelector('.radio-container');
    if (radioContainer) {
      radioContainer.addEventListener('click', function (event) {
        event.stopPropagation();
        const radioInput = this.querySelector('input[name="purchase-option"]');
        if (radioInput && !radioInput.checked) {
          radioInput.checked = true;
          updatePurchaseOptionDisplay(radioInput.id);
          updateSubscriptionCardStates();
        }
      });
    }
  });

  function updatePurchaseOptionDisplay(optionId) {
    const deliveryFrequencyEls = enhancedSection.querySelectorAll('.delivery-frequency');
    // Re-query each time to avoid stale references after button innerHTML is replaced
    const btn = enhancedSection.querySelector('.add-to-cart');
    const currentSubscribeText = btn?.querySelector('.subscribe-text');
    const currentOnetimeText = btn?.querySelector('.onetime-text');

    if (currentSubscribeText && currentOnetimeText) {
      if (optionId === 'subscribe') {
        currentSubscribeText.style.display = 'inline';
        currentOnetimeText.style.display = 'none';
        deliveryFrequencyEls.forEach((el) => (el.style.display = 'block'));
      } else {
        currentSubscribeText.style.display = 'none';
        currentOnetimeText.style.display = 'inline';
        deliveryFrequencyEls.forEach((el) => (el.style.display = 'none'));
      }
    } else if (currentOnetimeText) {
      // Only one-time purchase available
      currentOnetimeText.style.display = 'inline';
      deliveryFrequencyEls.forEach((el) => (el.style.display = 'none'));
    }

    updateSubscriptionCardStates();
  }

  function updateSubscriptionCardStates() {
    subscriptionCards.forEach((card) => {
      const radioInput = card.querySelector('input[name="purchase-option"]');
      card.classList.toggle('active', !!radioInput?.checked);
    });
  }

  // Set initial state
  updateSubscriptionCardStates();
  const checkedOption = enhancedSection.querySelector('input[name="purchase-option"]:checked');
  if (checkedOption) {
    updatePurchaseOptionDisplay(checkedOption.id);
  } else if (onetimeText && !subscribeText) {
    onetimeText.style.display = 'inline';
  }
}

// ---------------------------------------------------------------------------
// Supply duration option handlers
// ---------------------------------------------------------------------------

function setupSupplyOptionHandlers() {
  const enhancedSection = document.querySelector('.enhanced-product-section');
  if (!enhancedSection) return;

  const supplyOptions = enhancedSection.querySelectorAll('.supply-option');
  supplyOptions.forEach((option) => {
    option.addEventListener('click', function () {
      supplyOptions.forEach((opt) => opt.classList.remove('active'));
      this.classList.add('active');
    });
  });
}

// ---------------------------------------------------------------------------
// Main initialisation
// ---------------------------------------------------------------------------

function translateFlavorOptions() {
  const locale = typeof window.getCurrentLocale === 'function' ? window.getCurrentLocale() : '';
  if (!locale || locale.startsWith('en-')) return;

  const flavorSelect = document.querySelector('[data-option-name="Flavor"], [data-option-name="Flavour"]');
  if (!flavorSelect) return;

  const flavorPosition = parseInt(flavorSelect.dataset.optionPosition);
  if (!flavorPosition) return;

  // Same locale variations as getTranslatedTabValue in enhanced-product-helpers.js
  const parts = locale.split('-');
  const variations = [
    locale,
    locale.toUpperCase(),
    locale.toLowerCase(),
    `${parts[0].toLowerCase()}-${(parts[1] || '').toUpperCase()}`
  ];

  function getFlavourFromTranslations(translations) {
    if (!translations || typeof translations !== 'object') return null;
    for (var i = 0; i < variations.length; i++) {
      var flavour = translations[variations[i]] && translations[variations[i]].variant && translations[variations[i]].variant.flavour;
      if (flavour && typeof flavour === 'string' && flavour.trim()) return flavour;
    }
    return null;
  }

  // Build map: option value → translated flavour
  var flavorMap = {};
  if (window.variantTranslations && window.productVariants) {
    Object.entries(window.variantTranslations).forEach(function(entry) {
      var flavour = getFlavourFromTranslations(entry[1]);
      if (!flavour) return;
      var variant = window.productVariants.find(function(v) { return String(v.id) === entry[0]; });
      if (!variant) return;
      var optionVal = variant['option' + flavorPosition];
      if (optionVal) flavorMap[optionVal] = flavour;
    });
  }

  // Also check current variant's translation directly (same pattern as tab translations)
  var currentTranslations = (window.variantTranslations || {})[String(window.selectedVariantId || '')];
  var directFlavour = getFlavourFromTranslations(currentTranslations);
  if (directFlavour) {
    var selectedOpt = flavorSelect.options[flavorSelect.selectedIndex];
    if (selectedOpt) flavorMap[selectedOpt.value] = directFlavour;
  }

  // Update option text only — value attribute stays unchanged so selection logic is unaffected
  Array.from(flavorSelect.options).forEach(function(opt) {
    if (flavorMap[opt.value]) opt.textContent = flavorMap[opt.value];
  });
}

function translateSizeOptions() {
  const locale = typeof window.getCurrentLocale === 'function' ? window.getCurrentLocale() : '';
  if (!locale || locale.startsWith('en-')) return;

  // Same locale variations as translateFlavorOptions
  const parts = locale.split('-');
  const variations = [
    locale,
    locale.toUpperCase(),
    locale.toLowerCase(),
    `${parts[0].toLowerCase()}-${(parts[1] || '').toUpperCase()}`
  ];

  function getSizeFromTranslations(translations) {
    if (!translations || typeof translations !== 'object') return null;
    for (var i = 0; i < variations.length; i++) {
      var size = translations[variations[i]] && translations[variations[i]].variant && translations[variations[i]].variant.variant_size;
      if (size && typeof size === 'string' && size.trim()) return size;
    }
    return null;
  }

  // Find all non-flavor/color selects to translate (size option may have any name)
  var variantSelector = document.querySelector('[data-variant-selector]');
  if (!variantSelector) return;
  var allSelects = variantSelector.querySelectorAll('.variant-select');
  var sizeSelects = Array.from(allSelects).filter(function(s) {
    var name = (s.dataset.optionName || '').toLowerCase();
    return name !== 'flavour' && name !== 'flavor' && name !== 'colour' && name !== 'color';
  });
  if (!sizeSelects.length) return;

  // Build map: option value → translated size, keyed by option position
  var sizeMaps = {};
  if (window.variantTranslations && window.productVariants) {
    Object.entries(window.variantTranslations).forEach(function(entry) {
      var size = getSizeFromTranslations(entry[1]);
      if (!size) return;
      var variant = window.productVariants.find(function(v) { return String(v.id) === entry[0]; });
      if (!variant) return;
      sizeSelects.forEach(function(s) {
        var pos = parseInt(s.dataset.optionPosition);
        var optionVal = variant['option' + pos];
        if (optionVal) {
          sizeMaps[pos] = sizeMaps[pos] || {};
          sizeMaps[pos][optionVal] = size;
        }
      });
    });
  }

  // Update option text only — value attribute stays unchanged so selection logic is unaffected
  sizeSelects.forEach(function(sizeSelect) {
    var pos = parseInt(sizeSelect.dataset.optionPosition);
    var sizeMap = sizeMaps[pos] || {};
    Array.from(sizeSelect.options).forEach(function(opt) {
      if (sizeMap[opt.value]) opt.textContent = sizeMap[opt.value];
    });
  });
}

function translateLiquidDeliveryOptions() {
  if (typeof window.translateFrequencyText !== 'function') return;
  const deliverySelect = document.querySelector('[data-delivery-frequency]');
  if (!deliverySelect) return;
  // Only translate the Liquid-rendered dropdown, not the DMO-managed one
  if (deliverySelect.closest('[data-dmo-delivery-frequency]')) return;
  const locale = typeof window.getCurrentLocale === 'function' ? window.getCurrentLocale() : 'en-us';
  Array.from(deliverySelect.options).forEach(function(opt) {
    const translated = window.translateFrequencyText(opt.textContent.trim(), locale);
    if (translated) opt.textContent = translated;
  });
}

function initializeEnhancedProductInteractions() {
  setupPurchaseOptionHandlers();
  setupSupplyOptionHandlers();
  setupCustomAddToCartHandlers(); // enhanced-product-cart.js
  if (typeof setupVariantSelectorHandlers === 'function') {
    setupVariantSelectorHandlers(); // enhanced-variants-handle.js
  }
  translateLiquidDeliveryOptions();
  translateFlavorOptions();
  translateSizeOptions();
}

function initWhenReady() {
  const enhancedSection = document.querySelector('.enhanced-product-section');
  if (!enhancedSection) return;

  if (window.enhancedIsProductPage) {
    // Ensure selectedVariantId is populated if somehow not set yet
    window.selectedVariantId =
      window.selectedVariantId || window.enhancedProductData?.defaultVariantId;
  }

  initializeEnhancedProductInteractions();

  if (typeof applyButtonTranslations === 'function') applyButtonTranslations();

  if (window.enhancedIsProductPage) {
    if (typeof applyProductTranslations === 'function') applyProductTranslations();
    applyTabTranslationsWithRetry();
  } else {
    if (typeof applyDMOTranslations === 'function') applyDMOTranslations();
    applyTabTranslationsWithRetry();
  }
}

// ---------------------------------------------------------------------------
// DMO translation polling (non-product pages only)
// ---------------------------------------------------------------------------

if (!window.enhancedIsProductPage) {
  let dmoCheckAttempts = 0;
  const maxDMOChecks = 20; // ~4 s at 200 ms intervals

  function checkAndApplyDMOTranslations() {
    if (window.data?.theme?.productOffer?.offers) {
      if (typeof applyDMOTranslations === 'function') applyDMOTranslations();
    } else if (dmoCheckAttempts < maxDMOChecks) {
      dmoCheckAttempts++;
      setTimeout(checkAndApplyDMOTranslations, 200);
    }
  }

  setTimeout(checkAndApplyDMOTranslations, 100);
}

// ---------------------------------------------------------------------------
// Auto-initialise — this script is deferred, so the DOM is already ready
// ---------------------------------------------------------------------------

updatePromoPrice();
initWhenReady();

// Re-apply flavor and size translations after async variant filtering rebuilds the select options
document.addEventListener('variant:filters:applied', function () {
  translateFlavorOptions();
  translateSizeOptions();
});

// Re-apply size translations after user-driven variant changes rebuild dependent selects
document.addEventListener('variant:change', function () {
  translateSizeOptions();
});
